<template>
    <vx-box :disabled="loading" class="flex flex-col p-6 relative">
        <slot name="title">
            <span v-if="props.type === 'worst'">What's <strong>not</strong> performing</span>
            <span v-else>What's performing</span>
        </slot>

        <div v-if="loading" class="justify-center flex min-h-48 items-center">
            <vx-icon-loading class="w-4 h-4 animate-loading-spin"></vx-icon-loading>
        </div>

        <vx-alert v-else-if="error" :closeable="false" color="danger" class="mt-6">
            <template v-slot:icon>
                <font-awesome-icon widthAuto :icon="faTriangleExclamation" />
            </template>
            Error loading data
        </vx-alert>

        <div v-else-if="messages && messages.length" class="flex flex-col gap-6 py-6">
            <div v-for="message in messages" :key="message.resource_id" class="flex flex-col gap-2">
                <div class="flex flex-row justify-between items-center gap-4">
                    <span class="text-sm font-semibold truncate">{{ message.resource_name }}</span>
                </div>

                <message-cloud
                    :message="{ direction: 'outbound', ...message }"
                    :outboundColorClasses="type === 'worst' ? 'bg-slate-100 border-slate-200' : undefined"
                    fullWidth
                    :showDetails="false"
                    @attachmentClick="
                        ($event) => {
                            if ($event.isFileExpired) {
                                mediaExpiredModal = true;
                            }
                        }
                    "
                ></message-cloud>
            </div>
        </div>

        <vx-box v-else class="bg-slate-100! mt-6 px-8 py-20 gap-6 text-xl text-slate-500">
            <div class="mx-auto text-center">Sorry not enough data yet.</div>
        </vx-box>

        <expired-media-modal v-model="mediaExpiredModal"></expired-media-modal>
    </vx-box>
</template>

<script setup>
import { VxAlert, VxBox, VxIconLoading } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import { metricsInputValidator, metricsWithGoals } from '~/utils/analytics';
import { analyticsCategoriesValues } from '~/constants/categories';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { outboundMessagesAnalyticsClient } from '~/services';
import MessageCloud from '~/components/messages/browser-extension/components/misc/MessageCloud.vue';
import ExpiredMediaModal from '~/components/modals/ExpiredMediaModal.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';

const teamId = Spark.state.currentTeam.id;

const { loading, response, error, fetchData } = useVersionedRequest();

const mediaExpiredModal = ref(false);

const props = defineProps({
    category: {
        type: String,
        required: true,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    metric: {
        type: String,
        required: true,
        validator: (metric) => metricsWithGoals.includes(metric),
    },
    type: {
        type: String,
        required: true,
        validator: (type) => ['best', 'worst'].includes(type),
    },
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
});

const messages = computed(() => {
    return (response.value?.data?.[props.type] || []).map((resource) => ({
        ...resource,
        body: resource.message_body,
    }));
});

watch(
    () => props.fetchParams,
    () => {
        fetchData(
            outboundMessagesAnalyticsClient.getCategoryMetricNotableMessages(
                { teamId },
                props.category,
                props.metric,
                props.fetchParams
            )
        ).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);
</script>
